import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import SEO from '../components/SEO';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '40vh',
  minHeight: '300px',
  color: 'white',
  textAlign: 'center',
  overflow: 'hidden',
  background: 'linear-gradient(45deg, #3c005a 30%, #5c0080 90%)',
  padding: theme.spacing(8, 0),
}));

const BannerImage = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'opacity 1s ease-in-out',
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
  },
}));

const safetyDatabaseStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Thrana Safety Database',
  url: 'https://www.thranatech.com/products/safety-database',
  description:
    'Thrana Safety Database is a modern, automated, compliance-ready pharmacovigilance platform that streamlines intake-to-submission workflows for global life sciences teams.',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web-based',
  publisher: {
    '@type': 'Organization',
    name: 'Thranatech.com',
    url: 'https://www.thranatech.com',
  },
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
};

const SafetyDatabase = () => {
  const [currentBanner, setCurrentBanner] = useState(0);

  const banners = [
    '/banner1.jpg',
    '/banner2.jpg',
    '/Products-safety database1.jpg',
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <SEO
        title="Thrana Safety Database | Modern Automated PV Platform"
        description="Thrana Safety Database delivers end-to-end automation, lower TCO, configurable workflows, and global compliance readiness for pharmacovigilance teams."
        path="/products/safety-database"
        structuredData={safetyDatabaseStructuredData}
      />
      <Box>
      <HeroSection>
        {banners.map((banner, index) => (
          <BannerImage
            key={index}
            sx={{
              backgroundImage: `url(${banner})`,
              opacity: currentBanner === index ? 1 : 0,
            }}
          />
        ))}
        <Overlay>
          <Container maxWidth="md">
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              Thrana Safety Database
            </Typography>
            <Typography
              variant="h6"
              paragraph
              sx={{
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                color: 'white',
              }}
            >
              Modern, automated, compliance-ready pharmacovigilance
            </Typography>
          </Container>
        </Overlay>
      </HeroSection>

      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, height: '100%' }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#3c005a' }}>
                  The Problem We Solve
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Pharmacovigilance is growing in complexity, yet most legacy tools haven’t kept up. Thrana Safety Database eliminates bottlenecks caused by:
                </Typography>
                <ul style={{ paddingLeft: 24, marginTop: 0 }}>
                  {[
                    'Unstructured data streaming in from multiple sources',
                    'Rising volume of adverse event reports',
                    'Evolving global regulations and guidance updates',
                    'Manual, error-prone workflows that slow throughput',
                    'Rigid legacy systems that cost more and deliver less',
                  ].map((item) => (
                    <li key={item}>
                      <Typography variant="body1">{item}</Typography>
                    </li>
                  ))}
                </ul>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, height: '100%' }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#3c005a' }}>
                  Our Solution — Why Thrana?
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  A next-generation PV system built for today’s life sciences environment.
                </Typography>
                <ul style={{ paddingLeft: 24, marginTop: 0 }}>
                  {[
                    'End-to-end automation from intake to submission',
                    'Cloud-native architecture with significantly lower total cost of ownership',
                    'Highly configurable workflows—no expensive custom builds',
                    'Global compliance–ready (21 CFR Part 11, ICH, eVAERS, eMDR, MIR, IDMP)',
                    'Seamless API integrations with internal and external systems',
                    'Enterprise-grade security (HIPAA, ISO 27001, AWS HDS, 99.99% uptime)',
                  ].map((item) => (
                    <li key={item}>
                      <Typography variant="body1">{item}</Typography>
                    </li>
                  ))}
                </ul>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={4} sx={{ mt: 6 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, height: '100%' }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#3c005a' }}>
                  Business Benefits
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Built for business leaders looking for predictability, scale, and cost-efficiency.
                </Typography>
                <ul style={{ paddingLeft: 24, marginTop: 0 }}>
                  {[
                    'Lower total cost of ownership through modular pricing',
                    'Faster deployments and minimal IT dependency',
                    'Scalability across regions, partners, products, and case volumes',
                    'Integrated reporting for quicker decision-making',
                    'Enhanced compliance posture with global regulatory support',
                  ].map((item) => (
                    <li key={item}>
                      <Typography variant="body1">{item}</Typography>
                    </li>
                  ))}
                </ul>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, height: '100%' }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#3c005a' }}>
                  Operational Benefits
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Built for PV teams who need speed, accuracy, and control.
                </Typography>
                <ul style={{ paddingLeft: 24, marginTop: 0 }}>
                  {[
                    'Major reduction in manual workload',
                    'Automated triage, duplicate check, and XML validation',
                    'Integrated MedDRA/WHODD coding and smart data management',
                    'Faster narrative creation with multi-template and auto-narrative support',
                    'Smooth migration from E2B R2/R3 & legacy systems',
                    'Full audit trail and version control for every action',
                  ].map((item) => (
                    <li key={item}>
                      <Typography variant="body1">{item}</Typography>
                    </li>
                  ))}
                </ul>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: 6 }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#3c005a' }}>
              Key Product Features
            </Typography>
            <Grid container spacing={4} sx={{ mt: 2 }}>
              {[
                {
                  title: 'Case Processing',
                  bullets: [
                    'Automated intake-to-submission workflows',
                    'Smart triage, duplicate detection, and validation',
                    'MedDRA/WHODD coding assistance and QC',
                  ],
                },
                {
                  title: 'Administration & Configuration',
                  bullets: [
                    'Dynamic workflow builder with role-based rules',
                    'Configurability without costly custom development',
                    'Template and rules library for rapid updates',
                  ],
                },
                {
                  title: 'Security & Infrastructure',
                  bullets: [
                    'HIPAA, ISO 27001, AWS HDS compliant',
                    '99.99% uptime commitment',
                    'Granular access control and audit logs',
                  ],
                },
                {
                  title: 'Reporting',
                  bullets: [
                    'Integrated dashboards for operations and leadership',
                    'Regulatory-ready outputs with minimal effort',
                    'APIs for data exchange with BI ecosystems',
                  ],
                },
                {
                  title: 'Data Migration',
                  bullets: [
                    'Guided migration from E2B R2/R3 & legacy systems',
                    'Automated data validation and reconciliation',
                    'Comprehensive version control for every import',
                  ],
                },
              ].map((feature) => (
                <Grid item xs={12} md={6} key={feature.title}>
                  <FeatureCard elevation={3}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                        {feature.title}
                      </Typography>
                      <ul style={{ paddingLeft: 20, margin: 0 }}>
                        {feature.bullets.map((bullet) => (
                          <li key={bullet}>
                            <Typography variant="body2">{bullet}</Typography>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ mt: 8, textAlign: 'center' }}>
            <Button component={Link} to="/contact" size="large">
              Explore the Thrana Safety Database
            </Button>
          </Box>
        </Container>
      </Box>
      </Box>
    </>
  );
};

export default SafetyDatabase;

